"use client";

import { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
export default function ComplianceFaq() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqsEn = [{
    question: "What international standards do you comply with?",
    answer: "We maintain rigorous compliance with global safety standards, including ASTM, EN71, and ISO 9001. Our manufacturing processes are audited regularly to ensure consistent quality and safety for all consumer goods."
  }, {
    question: "Which testing laboratories do you partner with?",
    answer: "We work exclusively with accredited third-party testing facilities including Bureau Veritas (BV), Intertek (ITS), SGS, and TUV Rheinland to provide comprehensive certification and safety verification."
  }, {
    question: "How do you handle market-specific certifications?",
    answer: "Our team manages the full certification lifecycle, including CCC for the Chinese market and G-Mark for Gulf States, ensuring your products meet all regional regulatory requirements before shipment."
  }, {
    question: "Can you assist with product safety documentation?",
    answer: "Yes, we provide full documentation support, including Test Reports, Certificates of Conformity, and technical files required for customs clearance and retail distribution in international markets."
  }, {
    question: "What is your approach to quality control during production?",
    answer: "Quality is integrated into every stage of our manufacturing process. From raw material inspection to final pre-shipment audits, our internal QC teams ensure every unit meets your exact specifications."
  }, {
    question: "Do you provide support for Canadian market regulations?",
    answer: "Absolutely. We are well-versed in Canadian consumer product safety regulations and can guide you through the necessary testing and labeling requirements for successful market entry."
  }, {
    question: "How often are your factory facilities audited?",
    answer: "Our owned plastic and plush factories undergo annual social and technical audits to maintain compliance with international ethical manufacturing standards and quality management systems."
  }, {
    question: "What happens if a product fails a compliance test?",
    answer: "In the event of a non-conformance, we immediately halt production to identify the root cause. We work closely with our lab partners to implement corrective actions and re-test until full compliance is achieved."
  }];
  const faqsZh = [{
    question: "貴公司符合哪些國際標準？",
    answer: "我們嚴格遵守全球安全標準，包括 ASTM、EN71 及 ISO 9001。我們的生產流程定期接受審核，確保所有消費品的品質與安全始終如一。"
  }, {
    question: "貴公司與哪些檢測實驗室合作？",
    answer: "我們專門與獲認可的第三方檢測機構合作，包括必維國際檢驗集團（BV）、天祥公證行（ITS）、SGS 及德國萊茵（TUV Rheinland），提供全面的認證與安全驗證。"
  }, {
    question: "你們如何處理特定市場的認證？",
    answer: "我們的團隊管理整個認證週期，包括中國市場的 CCC 認證及海灣阿拉伯國家的 G-Mark 認證，確保您的產品在出貨前符合所有地區法規要求。"
  }, {
    question: "你們能否協助提供產品安全文件？",
    answer: "可以，我們提供全面的文件支援，包括測試報告、符合性證書（CoC）以及國際市場清關與零售分銷所需的技術檔案。"
  }, {
    question: "你們在生產過程中的品質控制流程是怎樣的？",
    answer: "品質把控融入我們生產過程的每一個階段。從原材料檢驗到最終出貨前審核，我們的內部品控團隊確保每一件產品均符合您的嚴格規格。"
  }, {
    question: "你們是否支援加拿大市場的法規要求？",
    answer: "當然。我們深諳加拿大消費品安全法規（CCPSA），能引導您完成必要的測試及標籤合規要求，助您的產品順利進入市場。"
  }, {
    question: "你們的工廠設施多久審核一次？",
    answer: "我們旗下的塑膠及毛絨玩具工廠每年均會進行社會責任及技術審核，以持續符合國際道德製造標準及品質管理體系。"
  }, {
    question: "如果產品未能通過合規測試會如何處理？",
    answer: "一旦出現不符合項，我們會立即暫停相關生產以查明根本原因，並與合作實驗室緊密配合，落實糾正措施並重新測試，直至完全達到合規標準為止。"
  }];
  const faqs = isZh ? faqsZh : faqsEn;
  return <section data-section-id="21249" id="compliance-faq" className="relative overflow-hidden bg-background py-24 md:py-36 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-50">
        <div className="absolute -top-32 right-[-10%] h-[520px] w-[520px] rounded-lg opacity-70 blur-3xl" style={{
        background: "radial-gradient(circle, hsl(44 92% 53% / 0.15), transparent 70%)"
      }} />
        <div className="absolute bottom-[-15%] left-[-8%] h-[600px] w-[600px] rounded-lg opacity-70 blur-3xl" style={{
        background: "radial-gradient(circle, hsl(160 84% 39% / 0.1), transparent 70%)"
      }} />
      </div>

      <div ref={ref} className="relative mx-auto max-w-[1240px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
          {}
          <div className={`lg:col-span-4 lg:order-1 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: "0ms"
        }}>
            <div className="lg:sticky lg:top-[120px]">
              <h2 className="font-default text-foreground" style={{
              fontSize: "clamp(2rem, 4vw, 3.5rem)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              lineHeight: 1.08
            }}>
                {isZh ? <>
                    合規{" "}
                    <span className={`font-default italic transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"}`} style={{
                  fontWeight: 400,
                  transitionDelay: "200ms"
                }}>
                      諮詢
                    </span>
                  </> : <>
                    Compliance{" "}
                    <span className={`font-default italic transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"}`} style={{
                  fontWeight: 400,
                  transitionDelay: "200ms"
                }}>
                      Inquiries
                    </span>
                  </>}
              </h2>

              <p className="mt-5 font-default text-muted-foreground" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
              fontWeight: 400,
              lineHeight: 1.6,
              letterSpacing: "-0.005em"
            }}>
                {isZh ? "關於我們製造標準、全球認證及嚴謹品質保證流程的常見問題解答。" : "Answers to common questions regarding our manufacturing standards, global certifications, and rigorous quality assurance protocols."}
              </p>

              <div className="mt-7">
                <Link to="#">
                  <Button variant="outline" className="h-auto rounded-lg border-primary bg-primary px-7 py-3.5 font-default text-sm font-semibold text-primary-foreground transition-all duration-200 hover:bg-primary/90">
                    {isZh ? "聯絡我們團隊" : "Contact Our Team"}
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-8 lg:order-2 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: "120ms"
        }}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((item, i) => <AccordionItem key={i} data-index={i} value={`faq-${i}`} className="border-b border-border last:border-b-0">
                  <AccordionTrigger className="group flex w-full items-start justify-between gap-6 py-7 text-right hover:no-underline [&>svg]:hidden">
                    <span className="flex-1 font-default text-foreground" style={{
                  fontSize: "1.0625rem",
                  fontWeight: 600,
                  lineHeight: 1.4,
                  letterSpacing: 0
                }}>
                      {item.question}
                    </span>
                    <span className="relative mt-1 flex h-5 w-5 shrink-0 items-center justify-center">
                      {}
                      <span className="absolute h-[1.5px] w-5 bg-foreground/70 transition-all duration-300 ease-out" />
                      {}
                      <span className="absolute h-5 w-[1.5px] bg-foreground/70 transition-all duration-300 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-7 pt-1">
                    <p className="max-w-[640px] font-default text-muted-foreground" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 400,
                  lineHeight: 1.65,
                  letterSpacing: 0
                }}>
                      {item.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}